import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Sparkles,
  Send,
  Trash2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  X,
  FileText,
  Download,
} from 'lucide-react';
import { dynamicReportsAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { formatDateTime as formatDate } from '../utils/dateHelpers';
import { downloadBlob } from '../utils/csv';
import DynamicReportChart, { ChartLayout } from './DynamicReportChart';
import { InlineLoader } from './loading';

type DateAnchor = 'today' | 'start_of_week' | 'start_of_month' | 'start_of_year';

interface ReportParameter {
  name: string;
  type: 'date' | 'select';
  label: string;
  // date-only
  default_relative_days?: number | null;
  default_anchor?: DateAnchor | null;
  default_offset_days?: number | null;
  // select-only
  options_sql?: string | null;
  default_value?: string | null;
}

interface DynamicReport {
  id: number;
  title: string;
  description: string | null;
  prompt: string;
  generated_sql: string;
  chart_config: ChartLayout;
  status: 'draft' | 'published';
  source_key: string | null;
  parameters: ReportParameter[];
  created_by_emp_code: string;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  last_run_at: string | null;
  last_run_error: string | null;
}

interface ChatMessage {
  id: number;
  report_id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

interface RunData {
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number;
  truncated: boolean;
  // The LIMIT actually applied. A report may carry a larger explicit LIMIT than
  // the engine default, so the badge must name the real number rather than a
  // hardcoded 5000 — which used to appear on any result over 5000 rows even when
  // nothing had been truncated.
  rowLimit?: number;
  // ── Preview slice ──
  // `rows` carries at most preview_row_limit entries however many the query
  // matched; rowCount stays the TRUE total. A wide date range on a per-day report
  // returns tens of thousands of rows across ~30 columns, which is millions of DOM
  // cells and tens of MB of JSON — enough to crash the tab outright. The full set
  // is reachable through the .xlsx export instead.
  preview_row_count?: number;
  preview_truncated?: boolean;
  preview_row_limit?: number;
}

interface Props {
  // Stable identifier matching the backend REPORT_SOURCES key.
  sourceKey: string;
  // Human-readable label shown throughout the UI ("Daily Attendance").
  sourceLabel: string;
  // Optional placeholder shown inside the composer textarea.
  promptPlaceholder?: string;
  // Optional secondary scoping key. When set, the panel only lists / generates
  // reports tagged with this exact context (e.g. a specific task_id on the
  // task-response per-task drill-down page). Reports created with a contextId
  // are invisible from the main source page, and vice versa — the backend
  // enforces this via the context_id leak check on every endpoint.
  contextId?: string;
  // The page's existing raw-data UI is passed in as children. It renders
  // inside the "Raw Data" tab and stays mounted (display toggled via CSS)
  // when the user switches to a custom-report tab so its filters survive.
  children: ReactNode;
}

/**
 * The export endpoint answers a failed query as JSON, but the request asked for a
 * blob — so axios hands back a Blob containing JSON. Read it back as text to
 * recover the server's message instead of rendering "[object Blob]".
 */
/**
 * A Blob's text, without assuming Blob.text() exists — jsdom's Blob has no text()
 * even though every browser does, so the FileReader path is what keeps this
 * testable rather than only working in production.
 */
const blobText = async (b: Blob): Promise<string> => {
  if (typeof (b as { text?: () => Promise<string> }).text === 'function') {
    return (b as unknown as { text: () => Promise<string> }).text();
  }
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(b);
  });
};

export const errorMessageFromBlob = async (e: unknown): Promise<string> => {
  const body = (e as { response?: { data?: unknown } })?.response?.data;
  // Tag-checked rather than `instanceof Blob`: instanceof is false for a Blob made
  // in another realm (a test file, an iframe), and the message would be lost.
  if (body && Object.prototype.toString.call(body) === '[object Blob]') {
    try {
      const parsed = JSON.parse(await blobText(body as Blob)) as { message?: string };
      if (parsed?.message) return parsed.message;
    } catch {
      /* not JSON (an HTML 502 page, say) — fall through to the generic message */
    }
  }
  return errorMessage(e);
};

const errorMessage = (e: unknown): string => {
  const anyErr = e as any;
  return anyErr?.response?.data?.message || anyErr?.message || 'Something went wrong.';
};

// When the model refuses with a "REFUSAL PROTOCOL" response, the backend
// includes 1-3 alternative prompts the user could send instead. Surface
// them as one-tap choices so the user doesn't have to compose from scratch.
const errorAlternatives = (e: unknown): string[] => {
  const raw = (e as any)?.response?.data?.alternatives;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a: unknown): a is string => typeof a === 'string' && a.trim().length > 0)
    .slice(0, 3);
};

// When the auto-retry loop gives up (e.g. validator failure on all 3
// attempts), the backend includes the SQL of the last attempt in
// `generated_sql`. Pulling it out lets the UI offer a "Show generated SQL"
// disclosure so the user can see exactly what tripped the validator
// without opening DevTools.
const errorGeneratedSql = (e: unknown): string | null => {
  const raw = (e as any)?.response?.data?.generated_sql;
  return typeof raw === 'string' && raw.trim().length > 0 ? raw : null;
};

// Compute a YYYY-MM-DD default for a date parameter. Mirrors the server-
// side `defaultValueFor` so the controls and the server agree:
//   - `default_anchor` (preferred) anchors to today / start-of-week (Mon)
//      / start-of-month / start-of-year, then applies `default_offset_days`.
//   - Legacy `default_relative_days` is "today + n days" when no anchor.
const defaultDateValueFor = (p: ReportParameter): string => {
  const now = new Date();
  const istMs = now.getTime() + 5.5 * 60 * 60 * 1000;
  const ist = new Date(istMs);
  let base = new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()));
  switch (p.default_anchor) {
    case 'start_of_week': {
      const dow = base.getUTCDay();
      const daysSinceMon = dow === 0 ? 6 : dow - 1;
      base.setUTCDate(base.getUTCDate() - daysSinceMon);
      break;
    }
    case 'start_of_month':
      base = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1));
      break;
    case 'start_of_year':
      base = new Date(Date.UTC(base.getUTCFullYear(), 0, 1));
      break;
    default:
      // 'today' or undefined — leave base alone
      break;
  }
  const offset = p.default_anchor != null
    ? (p.default_offset_days ?? 0)
    : (p.default_relative_days ?? 0);
  base.setUTCDate(base.getUTCDate() + offset);
  const y = base.getUTCFullYear();
  const m = String(base.getUTCMonth() + 1).padStart(2, '0');
  const d = String(base.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const initialParamValues = (parameters: ReportParameter[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const p of parameters) {
    if (p.type === 'date') out[p.name] = defaultDateValueFor(p);
    else if (p.type === 'select') out[p.name] = p.default_value ?? '';
  }
  return out;
};

// After the server returns dropdown options for select parameters, fill
// in any blank select values with the first option so the chart can run
// (the backend rejects empty select values). User-provided values are
// preserved.
const reconcileParamValuesWithOptions = (
  values: Record<string, string>,
  parameters: ReportParameter[],
  options: Record<string, { options: string[]; error?: string }>
): Record<string, string> => {
  const next = { ...values };
  for (const p of parameters) {
    if (p.type !== 'select') continue;
    if (next[p.name] && next[p.name].length > 0) continue;
    const opts = options[p.name]?.options ?? [];
    if (opts.length > 0) next[p.name] = opts[0];
  }
  return next;
};

type TabId = 'raw' | number;

export default function CustomReportPanel({
  sourceKey,
  sourceLabel,
  promptPlaceholder,
  contextId,
  children,
}: Props) {
  const { user } = useAuthStore();
  // Platform-standard primary colour shown across every modal / panel surface
  // (matches the Edit Task modal header, etc.). Stays the same regardless of
  // which project is active — per-project theme colours are reserved for
  // branding surfaces like the email header, not in-app chrome.
  const primaryColor = '#1B3A54';
  // canAuthor is the authoritative gate for showing the composer FAB and
  // the "My Drafts" surface. It is granted by:
  //   (a) being super_admin, or
  //   (b) a role of the selected project with one of the AI report pages ticked
  //       in Access Management → Web Access (the page tick carries the right).
  // Until /access resolves, default to the optimistic admin-role hint so
  // the FAB doesn't briefly disappear for users who do have the flag.
  const optimisticAdmin = user?.adminRole === 'admin' || user?.adminRole === 'super_admin';
  const [canAuthor, setCanAuthor] = useState<boolean>(optimisticAdmin);
  // Active project from the global header dropdown. Custom reports are
  // bound to the project they were created in, so this drives both
  // visibility (which tabs appear) and creation (server requires it).
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId) ?? undefined;

  const [drafts, setDrafts] = useState<DynamicReport[]>([]);
  const [published, setPublished] = useState<DynamicReport[]>([]);
  const [, setLoadingLists] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<TabId>('raw');

  const [composerOpen, setComposerOpen] = useState(false);
  // composerMode controls what the popover shows. 'auto' = derive from
  // active tab (Raw Data → new report composer, custom-report tab → that
  // report's chat thread). 'new' overrides to force the new-report
  // composer regardless of active tab (used by the "+ New" button so a
  // user viewing one report can start a fresh one without leaving the tab).
  const [composerMode, setComposerMode] = useState<'auto' | 'new'>('auto');
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generateAlternatives, setGenerateAlternatives] = useState<string[]>([]);
  const [generateFailedSql, setGenerateFailedSql] = useState<string | null>(null);
  const [generateFailedSqlOpen, setGenerateFailedSqlOpen] = useState(false);

  // The currently-active custom report (if activeTab is a number) and its
  // executed data. Refreshed on tab switch and on explicit "Re-run".
  const [tabReport, setTabReport] = useState<DynamicReport | null>(null);
  const [tabData, setTabData] = useState<RunData | null>(null);
  const [tabLoading, setTabLoading] = useState(false);
  const [tabError, setTabError] = useState<string | null>(null);
  // User-edited parameter values for the active tab (e.g. start_date /
  // end_date). Reset whenever activeTab changes; initialised to each
  // param's default. Sent to /run on every refresh; the chart updates
  // when the user clicks "Apply" inside the parameter strip.
  const [tabParamValues, setTabParamValues] = useState<Record<string, string>>({});
  // Server-resolved dropdown options for select-type parameters. Keyed by
  // parameter name. Empty / missing entries render an empty <select>.
  const [tabParamOptions, setTabParamOptions] = useState<
    Record<string, { options: string[]; error?: string }>
  >({});
  // Server-rendered column header labels keyed by static SQL alias —
  // e.g. { sales_m3: "february_2026" }. Empty when the report has no
  // dynamic-named columns. Refreshed on every run/generate/modify.
  const [tabColumnDisplay, setTabColumnDisplay] = useState<Record<string, string>>({});

  const refreshLists = async (): Promise<{ drafts: DynamicReport[]; published: DynamicReport[] }> => {
    // Without a selected project we can't show anything (per-project
    // visibility) — clear the lists and short-circuit.
    if (!selectedProjectId) {
      setPublished([]);
      setDrafts([]);
      return { published: [], drafts: [] };
    }
    setLoadingLists(true);
    setListError(null);
    try {
      const [pubRes, draftRes] = await Promise.all([
        dynamicReportsAPI.list({
          status: 'published',
          source_key: sourceKey,
          project_id: selectedProjectId,
          ...(contextId ? { context_id: contextId } : {}),
        }),
        canAuthor
          ? dynamicReportsAPI.list({
              status: 'draft',
              source_key: sourceKey,
              project_id: selectedProjectId,
              ...(contextId ? { context_id: contextId } : {}),
            })
          : Promise.resolve({ data: { reports: [] } } as any),
      ]);
      const pub = (pubRes.data.reports ?? []) as DynamicReport[];
      const drf = (draftRes.data.reports ?? []) as DynamicReport[];
      setPublished(pub);
      setDrafts(drf);
      return { published: pub, drafts: drf };
    } catch (e) {
      setListError(errorMessage(e));
      return { published: [], drafts: [] };
    } finally {
      setLoadingLists(false);
    }
  };

  useEffect(() => {
    // Resolve actual authoring permission from the backend, for the project on screen
    // (asked again when it changes). Falls back to the optimistic guess if the call
    // fails (the backend will still refuse the actual generate call so it's safe).
    let cancelled = false;
    dynamicReportsAPI
      .access(selectedProjectId)
      .then((res) => {
        if (cancelled) return;
        setCanAuthor(Boolean(res.data?.canAuthor));
      })
      .catch(() => {
        /* keep optimistic value */
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  // What the person is looking at: the source, the project and the per-context view.
  const viewKey = `${sourceKey}|${selectedProjectId ?? ''}|${contextId ?? ''}`;
  const shownViewKey = useRef<string | null>(null);
  useEffect(() => {
    refreshLists();
    // Back to Raw Data only when what is being looked at changes. Authoring access
    // resolving a moment after the page opens changes which drafts are listed, not
    // what is on screen: resetting the tab for it threw a person who had already
    // opened a report tab back to Raw Data.
    if (shownViewKey.current !== viewKey) {
      shownViewKey.current = viewKey;
      setActiveTab('raw');
    }
    // Refresh whenever the source switches, our access resolves, the user
    // picks a different project, or we navigate into/out of a per-context
    // (e.g. per-task) view. All of those change the visible report set.
  }, [sourceKey, canAuthor, selectedProjectId, contextId]);

  // Whenever the active tab changes, reset the manual mode override so the
  // popover follows the new tab's context automatically.
  useEffect(() => {
    setComposerMode('auto');
  }, [activeTab]);

  const allCustomTabs = useMemo<DynamicReport[]>(
    () => [...published, ...drafts],
    [published, drafts]
  );

  // When the user switches to a custom-report tab, fetch its run data.
  useEffect(() => {
    if (activeTab === 'raw') {
      setTabReport(null);
      setTabData(null);
      setTabError(null);
      setTabLoading(false);
      setTabParamOptions({});
      setTabColumnDisplay({});
      return;
    }
    const id = activeTab;
    const report = allCustomTabs.find((r) => r.id === id) ?? null;
    setTabReport(report);
    if (!report) {
      // The open report is no longer listed (deleted or unpublished by someone else, or
      // a draft the person turns out not to be allowed to see). With no report the view
      // below can only say "Loading report…" for ever, so go back to Raw Data.
      setActiveTab('raw');
      return;
    }
    // Reset param controls to the report's declared defaults whenever a
    // tab activates. The user can then tweak them via the strip above the
    // chart and click Apply to re-run.
    setTabParamValues(initialParamValues(report.parameters ?? []));
    setTabParamOptions({});
    setTabColumnDisplay({});
    setTabLoading(true);
    setTabError(null);
    // Initial run uses defaults — backend falls back when no `params`
    // body is sent, so we don't need to pass anything explicitly.
    dynamicReportsAPI
      .run(id, selectedProjectId, undefined, contextId)
      .then((res) => {
        setTabData(res.data.data);
        setTabReport(res.data.report);
        const opts = res.data.parameter_options ?? {};
        setTabParamOptions(opts);
        setTabColumnDisplay(res.data.column_display ?? {});
        setTabParamValues((prev) =>
          reconcileParamValuesWithOptions(prev, res.data.report.parameters ?? [], opts)
        );
      })
      .catch((e) => setTabError(errorMessage(e)))
      .finally(() => setTabLoading(false));
  }, [activeTab, allCustomTabs.length, selectedProjectId, contextId]);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setGenerateError(null);
    setGenerateAlternatives([]);
    setGenerateFailedSql(null);
    setGenerateFailedSqlOpen(false);
    try {
      // Server requires project_id (it's the project the report will be
      // bound to for life). The FAB is hidden when no project is picked,
      // so this should always be present here.
      if (!selectedProjectId) {
        setGenerateError('Pick a project from the header before generating a report.');
        setGenerating(false);
        return;
      }
      const res = await dynamicReportsAPI.generate(
        prompt,
        sourceKey,
        selectedProjectId,
        contextId
      );
      const newReport: DynamicReport = res.data.report;
      const preview: RunData = res.data.preview;
      await refreshLists();
      setComposerOpen(false);
      setPrompt('');
      setActiveTab(newReport.id);
      // Seed the tab state so the user sees the report immediately
      // without an extra round-trip — the run data we already have IS
      // the preview from the generate call. Init param controls too.
      setTabReport(newReport);
      setTabData(preview);
      const opts = res.data.parameter_options ?? {};
      setTabParamOptions(opts);
      setTabColumnDisplay(res.data.column_display ?? {});
      setTabParamValues(
        reconcileParamValuesWithOptions(
          initialParamValues(newReport.parameters ?? []),
          newReport.parameters ?? [],
          opts
        )
      );
      setTabError(null);
      setTabLoading(false);
    } catch (e) {
      setGenerateError(errorMessage(e));
      setGenerateAlternatives(errorAlternatives(e));
      setGenerateFailedSql(errorGeneratedSql(e));
    } finally {
      setGenerating(false);
    }
  };

  const publishCurrent = async () => {
    if (!tabReport) return;
    try {
      const res = await dynamicReportsAPI.publish(tabReport.id);
      setTabReport(res.data.report);
      await refreshLists();
    } catch (e) {
      alert(errorMessage(e));
    }
  };

  const unpublishCurrent = async () => {
    if (!tabReport) return;
    try {
      const res = await dynamicReportsAPI.unpublish(tabReport.id);
      setTabReport(res.data.report);
      await refreshLists();
    } catch (e) {
      alert(errorMessage(e));
    }
  };

  const deleteCurrent = async () => {
    if (!tabReport) return;
    if (!confirm(`Delete "${tabReport.title}"? This cannot be undone.`)) return;
    try {
      await dynamicReportsAPI.remove(tabReport.id);
      await refreshLists();
      setActiveTab('raw');
    } catch (e) {
      alert(errorMessage(e));
    }
  };

  // Download goes to the SERVER, not to the rows in memory. The preview is capped
  // (the run endpoint sends at most PREVIEW_ROW_LIMIT), so building a file from
  // `data.rows` would silently export a fraction of a wide date range — and pulling
  // the full set into the browser to avoid that is exactly what used to crash the
  // tab. The response is a blob, so the rows never become JavaScript objects.
  const [downloading, setDownloading] = useState(false);
  const downloadCurrent = async () => {
    if (!tabReport) return;
    setDownloading(true);
    setTabError(null);
    try {
      const res = await dynamicReportsAPI.exportXlsx(
        tabReport.id,
        selectedProjectId,
        Object.keys(tabParamValues).length > 0 ? tabParamValues : undefined,
        contextId
      );
      const slug = tabReport.title.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toLowerCase();
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(`${slug || 'custom_report'}_${date}.xlsx`, res.data as Blob);
    } catch (e) {
      // The server answers a failed QUERY as JSON even on this endpoint, but the
      // client asked for a blob — so read the blob back as text to recover the
      // message instead of showing "[object Blob]".
      setTabError(await errorMessageFromBlob(e));
    } finally {
      setDownloading(false);
    }
  };

  const rerunCurrent = async () => {
    if (!tabReport) return;
    setTabLoading(true);
    setTabError(null);
    try {
      const res = await dynamicReportsAPI.run(
        tabReport.id,
        selectedProjectId,
        Object.keys(tabParamValues).length > 0 ? tabParamValues : undefined,
        contextId
      );
      setTabData(res.data.data);
      setTabReport(res.data.report);
      const opts = res.data.parameter_options ?? {};
      setTabParamOptions(opts);
      setTabColumnDisplay(res.data.column_display ?? {});
      setTabParamValues((prev) =>
        reconcileParamValuesWithOptions(prev, res.data.report.parameters ?? [], opts)
      );
    } catch (e) {
      setTabError(errorMessage(e));
    } finally {
      setTabLoading(false);
    }
  };

  const placeholder = useMemo(
    () => promptPlaceholder ?? 'e.g. "Top 10 entries by count this month, as a bar chart"',
    [promptPlaceholder]
  );

  const showTabBar = allCustomTabs.length > 0;

  // Focus the textarea after the open animation starts so the cursor lands
  // in the right place but the transition is still visible.
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (composerOpen) {
      const t = setTimeout(() => textareaRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [composerOpen]);

  return (
    <>
      {showTabBar && (
        <div className="mb-4">
          <nav className="flex flex-wrap items-center gap-2">
            <TabPill
              active={activeTab === 'raw'}
              onClick={() => setActiveTab('raw')}
              icon={<FileText className="h-3.5 w-3.5" />}
              primaryColor={primaryColor}
            >
              {sourceLabel} (Raw Data)
            </TabPill>
            {allCustomTabs.map((r) => (
              <TabPill
                key={r.id}
                active={activeTab === r.id}
                onClick={() => setActiveTab(r.id)}
                icon={<Sparkles className="h-3.5 w-3.5" />}
                badge={r.status === 'draft' ? 'DRAFT' : undefined}
                primaryColor={primaryColor}
              >
                {r.title}
              </TabPill>
            ))}
          </nav>
        </div>
      )}

      {/* Raw data — always mounted, hidden when on a custom tab so its
          internal state (filters, search, pagination) survives a tab switch. */}
      <div className={activeTab === 'raw' ? '' : 'hidden'}>{children}</div>

      {activeTab !== 'raw' && (
        <CustomReportTabView
          sourceLabel={sourceLabel}
          report={tabReport}
          data={tabData}
          loading={tabLoading}
          error={tabError}
          isAdmin={canAuthor}
          onPublish={publishCurrent}
          onUnpublish={unpublishCurrent}
          onDelete={deleteCurrent}
          onRerun={rerunCurrent}
          onDownload={downloadCurrent}
          downloading={downloading}
          paramValues={tabParamValues}
          onParamChange={(name, value) =>
            setTabParamValues((prev) => ({ ...prev, [name]: value }))
          }
          parameterOptions={tabParamOptions}
          columnDisplay={tabColumnDisplay}
          primaryColor={primaryColor}
        />
      )}

      {listError && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {listError}
        </div>
      )}

      {canAuthor && selectedProjectId && (() => {
        // Resolve which mode the popover should render. 'modify' only when
        // the user is viewing a custom-report tab AND has not explicitly
        // asked for a fresh composer via the "+ New" button.
        const effectiveMode: 'new' | 'modify' =
          composerMode === 'new' || activeTab === 'raw' || !tabReport ? 'new' : 'modify';
        // Anyone with the authoring capability (the outer `canAuthor` guard already
        // requires it) may modify ANY report in the project — not just their own. The
        // creator-ownership restriction was removed so a granted role gets full edit
        // access, matching the server (dynamic-reports.controller.ts modifyReport).
        const canEditTabReport = tabReport != null;
        return (
        <div className="pointer-events-none fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
          {/* Chat-style composer popover. Always rendered so the open/close
              transition can animate; pointer-events disabled when closed so
              it doesn't intercept clicks behind it. Anchored to the FAB
              corner via origin-bottom-right + a small translate. */}
          <div
            aria-hidden={!composerOpen}
            className={`w-[24rem] max-w-[calc(100vw-3rem)] origin-bottom-right rounded-xl border border-gray-200 bg-white shadow-2xl transition-all duration-200 ease-out ${
              composerOpen
                ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
                : 'pointer-events-none translate-y-2 scale-95 opacity-0'
            }`}
          >
            <div
              className="flex items-start justify-between gap-3 rounded-t-xl border-b border-gray-200 p-3 text-white"
              style={{ backgroundColor: primaryColor }}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 flex-shrink-0" />
                  <h3 className="truncate text-sm font-semibold">
                    {effectiveMode === 'new'
                      ? `New ${sourceLabel} report`
                      : (tabReport?.title ?? `${sourceLabel} report`)}
                  </h3>
                </div>
                <p className="mt-0.5 text-xs text-white/80">
                  {effectiveMode === 'new'
                    ? `AI will only query ${sourceLabel.toLowerCase()} data.`
                    : 'Continue the conversation to refine this report.'}
                </p>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1">
                {effectiveMode === 'modify' && (
                  <button
                    onClick={() => {
                      setComposerMode('new');
                      setGenerateError(null);
                    }}
                    title="Start a new report"
                    aria-label="Start a new report"
                    className="rounded-md px-2 py-1 text-xs font-medium text-white/80 hover:bg-white/10 hover:text-white"
                  >
                    + New
                  </button>
                )}
                <button
                  onClick={() => {
                    setComposerOpen(false);
                    setGenerateError(null);
                  }}
                  aria-label="Close composer"
                  className="rounded-md p-1 text-white/80 hover:bg-white/10 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {effectiveMode === 'new' ? (
              <div className="space-y-3 p-3">
                <div className="text-[10px] font-medium uppercase tracking-wide text-blue-700">
                  Building from: {sourceLabel}
                </div>
                <textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={placeholder}
                  rows={4}
                  disabled={generating}
                  className="block w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500">⌘/Ctrl + Enter</span>
                  <button
                    onClick={handleGenerate}
                    disabled={generating || !prompt.trim()}
                    className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                    style={generating || !prompt.trim() ? undefined : { backgroundColor: primaryColor }}
                  >
                    {generating ? (
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    {generating ? 'Generating…' : 'Generate report'}
                  </button>
                </div>
                {generating && (
                  <div className="text-xs text-gray-500">
                    Usually 5–15 seconds. Opens as a new tab above.
                  </div>
                )}
                {generateError && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                    <div className="flex items-start gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">Cannot generate this report.</div>
                        <div className="mt-0.5 whitespace-pre-wrap">{generateError}</div>
                        {generateAlternatives.length > 0 && (
                          <div className="mt-2 space-y-1">
                            <div className="text-[11px] font-medium text-red-900">Try one of these instead:</div>
                            {generateAlternatives.map((alt, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => {
                                  setPrompt(alt);
                                  setGenerateError(null);
                                  setGenerateAlternatives([]);
                                  setGenerateFailedSql(null);
                                  setGenerateFailedSqlOpen(false);
                                  textareaRef.current?.focus();
                                }}
                                className="block w-full rounded border border-red-200 bg-white px-2 py-1 text-left text-xs text-gray-800 hover:border-blue-400 hover:bg-blue-50"
                              >
                                {alt}
                              </button>
                            ))}
                          </div>
                        )}
                        {generateFailedSql && (
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={() => setGenerateFailedSqlOpen((v) => !v)}
                              className="text-[11px] font-medium text-red-900 hover:underline"
                            >
                              {generateFailedSqlOpen ? 'Hide generated SQL' : 'Show generated SQL'}
                            </button>
                            {generateFailedSqlOpen && (
                              <pre className="mt-1 max-h-40 overflow-auto rounded bg-gray-900 p-2 text-[10px] leading-snug text-gray-100">
                                {generateFailedSql}
                              </pre>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : tabReport ? (
              <ModifyChatPanel
                report={tabReport}
                canEdit={canEditTabReport}
                sourceLabel={sourceLabel}
                onModified={(updatedReport, newData, options, columnDisplay) => {
                  setTabReport(updatedReport);
                  setTabData(newData);
                  if (options) {
                    setTabParamOptions(options);
                    setTabParamValues((prev) =>
                      reconcileParamValuesWithOptions(prev, updatedReport.parameters ?? [], options)
                    );
                  }
                  if (columnDisplay) setTabColumnDisplay(columnDisplay);
                  refreshLists();
                }}
              />
            ) : (
              <div className="p-3 text-xs text-gray-500"><InlineLoader label="Loading report…" size="xs" /></div>
            )}
          </div>

          <button
            onClick={() => {
              if (composerOpen) {
                setComposerOpen(false);
                setGenerateError(null);
              } else {
                setComposerOpen(true);
              }
            }}
            title={composerOpen ? 'Close composer' : `New custom ${sourceLabel} report`}
            aria-label={composerOpen ? 'Close composer' : `New custom ${sourceLabel} report`}
            aria-expanded={composerOpen}
            className={`pointer-events-auto inline-flex items-center gap-2 rounded-full px-4 py-3 text-sm font-medium text-white shadow-lg transition hover:shadow-xl ${
              composerOpen ? 'bg-gray-700 hover:bg-gray-800' : ''
            }`}
            style={composerOpen ? undefined : { backgroundColor: primaryColor }}
          >
            {composerOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Sparkles className="h-5 w-5" />
            )}
            <span className="hidden md:inline">
              {composerOpen ? 'Close' : 'New custom report'}
            </span>
          </button>
        </div>
        );
      })()}
    </>
  );
}

function TabPill({
  active,
  onClick,
  icon,
  badge,
  children,
  primaryColor,
}: {
  active: boolean;
  onClick: () => void;
  icon?: ReactNode;
  badge?: string;
  children: ReactNode;
  primaryColor: string;
}) {
  // Pill-shaped tabs that wrap to multiple rows instead of horizontally
  // scrolling — keeps every report visible at a glance even with 6-8
  // custom reports in play. Each pill caps at ~16rem with truncation +
  // a tooltip so very long titles ("Daily Sales vs. Daily Stock
  // Submission Duration (Last 30 Days)") stay readable.
  const tooltip = typeof children === 'string' ? children : undefined;
  return (
    <button
      onClick={onClick}
      title={tooltip}
      className={`group inline-flex max-w-[16rem] items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'text-white shadow-sm'
          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
      }`}
      style={active ? { backgroundColor: primaryColor, borderColor: primaryColor } : undefined}
    >
      <span
        className={`flex-shrink-0 ${active ? 'text-white' : ''}`}
        style={active ? undefined : { color: primaryColor }}
      >
        {icon}
      </span>
      {badge === 'DRAFT' && (
        <span
          aria-label="draft"
          title="Draft — not yet published"
          className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
            active ? 'bg-amber-300' : 'bg-amber-500'
          }`}
        />
      )}
      <span className="truncate">{children}</span>
    </button>
  );
}

function CustomReportTabView({
  sourceLabel,
  report,
  data,
  loading,
  error,
  isAdmin,
  onPublish,
  onUnpublish,
  onDelete,
  onRerun,
  onDownload,
  downloading,
  paramValues,
  onParamChange,
  parameterOptions,
  columnDisplay,
  primaryColor,
}: {
  sourceLabel: string;
  report: DynamicReport | null;
  data: RunData | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  onRerun: () => void;
  /** Fetches the FULL result set from the server as .xlsx — see downloadCurrent. */
  onDownload: () => void;
  downloading: boolean;
  paramValues: Record<string, string>;
  onParamChange: (name: string, value: string) => void;
  parameterOptions: Record<string, { options: string[]; error?: string }>;
  columnDisplay: Record<string, string>;
  primaryColor: string;
}) {
  if (!report) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
        <InlineLoader label="Loading report…" />
      </div>
    );
  }

  // Download the executed result set as CSV. Excel opens CSV natively and every
  // other export in the app is CSV (there is no xlsx dependency here), so this
  // stays consistent rather than pulling in a spreadsheet writer.
  //
  // The file mirrors the on-screen table: same column subset and order (a single
  // chart_config may pin `columns`), the same display labels, and the same
  // formatCell() the table uses. A multi-chart layout has no single column list,
  // so it exports the full result set — the export is of the DATA, not of one visual.
  // Kept as a thin alias so the button's call site reads the same as before; the
  // work now happens in the parent's downloadCurrent, which has the project and
  // context ids the export endpoint needs.
  const handleDownload = onDownload;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">{report.title}</h2>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                report.status === 'published'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-amber-100 text-amber-800'
              }`}
            >
              {report.status}
            </span>
            <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
              <Sparkles className="h-3 w-3 text-blue-600" /> from {sourceLabel}
            </span>
          </div>
          {report.description && (
            <p className="mt-1 text-sm text-gray-600">{report.description}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">Last run: {formatDate(report.last_run_at)}</p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
          <button
            onClick={onRerun}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" /> Re-run
          </button>
          {/* Not admin-gated — anyone who can see the report's rows on screen can
              already read them; withholding the file would only be friction. */}
          {data && data.rows.length > 0 && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              title="Download the full result as an Excel file — not just the rows shown here"
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              <Download className={`h-4 w-4 ${downloading ? 'animate-pulse' : ''}`} />
              {downloading ? 'Preparing…' : 'Download'}
            </button>
          )}
          {isAdmin && report.status === 'draft' && (
            <button
              onClick={onPublish}
              className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
            >
              <CheckCircle2 className="h-4 w-4" /> Publish
            </button>
          )}
          {isAdmin && report.status === 'published' && (
            <button
              onClick={onUnpublish}
              className="inline-flex items-center gap-1 rounded-md border border-amber-200 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50"
            >
              Unpublish
            </button>
          )}
          {isAdmin && (
            <button
              onClick={onDelete}
              className="inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
        </div>
      </div>

      {report.parameters && report.parameters.length > 0 && (
        <div className="mb-3 flex flex-wrap items-end gap-3 rounded-md border border-blue-100 bg-blue-50/40 p-3">
          {report.parameters.map((p) => {
            const selectMeta = p.type === 'select' ? parameterOptions[p.name] : undefined;
            return (
              <label key={p.name} className="flex flex-col gap-1 text-xs">
                <span className="font-medium uppercase tracking-wide text-blue-700">{p.label}</span>
                {p.type === 'date' ? (
                  <input
                    type="date"
                    value={paramValues[p.name] ?? ''}
                    onChange={(e) => onParamChange(p.name, e.target.value)}
                    disabled={loading}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                ) : p.type === 'select' ? (
                  <>
                    <select
                      value={paramValues[p.name] ?? ''}
                      onChange={(e) => onParamChange(p.name, e.target.value)}
                      disabled={loading || !selectMeta || selectMeta.options.length === 0}
                      className="min-w-[10rem] rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      {(!selectMeta || selectMeta.options.length === 0) && (
                        <option value="">{selectMeta?.error ? 'Failed to load' : 'Loading…'}</option>
                      )}
                      {selectMeta?.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    {selectMeta?.error && (
                      <span className="text-[10px] text-red-700" title={selectMeta.error}>
                        Couldn't load options
                      </span>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    value={paramValues[p.name] ?? ''}
                    onChange={(e) => onParamChange(p.name, e.target.value)}
                    disabled={loading}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                )}
              </label>
            );
          })}
          <button
            onClick={onRerun}
            disabled={loading}
            className="ml-auto inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
            style={loading ? undefined : { backgroundColor: primaryColor }}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Apply
          </button>
        </div>
      )}
      {loading && (
        <div className="py-12 text-center text-sm text-gray-500"><InlineLoader label="Running query…" /></div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">This report could not be run.</div>
              <div className="mt-1 whitespace-pre-wrap">{error}</div>
            </div>
          </div>
        </div>
      )}
      {data && !error && !loading && (
        <>
          <div className="mb-2 text-xs text-gray-500">
            {data.rowCount.toLocaleString()} row{data.rowCount === 1 ? '' : 's'}
            {/* The screen deliberately holds only a slice of a wide result — say so
                and point at the file, or 5,000 reads as the whole month. */}
            {data.preview_truncated && (
              <span className="ml-2 text-amber-700">
                showing the first {(data.preview_row_count ?? 0).toLocaleString()} here — use Download for all{' '}
                {data.rowCount.toLocaleString()}
              </span>
            )}
            {data.truncated && !data.preview_truncated && (
              <span className="ml-2 text-amber-700">
                (capped at {(data.rowLimit ?? 5000).toLocaleString()})
              </span>
            )}
          </div>
          <DynamicReportChart
            columns={data.columns}
            rows={data.rows}
            config={report.chart_config}
            columnDisplay={columnDisplay}
          />
        </>
      )}

    </div>
  );
}

// Per-report chat panel rendered inside the bottom-right popover when the
// user is viewing a custom-report tab. Each report owns its own thread —
// modifications happen here and update the same report in place. The
// popover itself provides the outer chrome (header + border).
function ModifyChatPanel({
  report,
  canEdit,
  onModified,
}: {
  report: DynamicReport;
  canEdit: boolean;
  sourceLabel: string;
  onModified: (
    report: DynamicReport,
    data: RunData,
    parameterOptions?: Record<string, { options: string[]; error?: string }>,
    columnDisplay?: Record<string, string>
  ) => void;
}) {
  const reportId = report.id;
  // Read the active project from the auth store. Used for the cross-
  // project leak check on /messages — the server returns 404 if the
  // active project doesn't match the report's persisted project.
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId) ?? undefined;
  const primaryColor = '#1B3A54';
  // The popover-mounted ModifyChatPanel doesn't currently know about the
  // contextId of its parent CustomReportPanel — it doesn't need to for
  // /messages calls because the backend's leak check uses project_id. If
  // we ever serve the same report id from multiple contexts this becomes
  // important; for now it's safe to omit.
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendAlternatives, setSendAlternatives] = useState<string[]>([]);
  const [sendFailedSql, setSendFailedSql] = useState<string | null>(null);
  const [sendFailedSqlOpen, setSendFailedSqlOpen] = useState(false);
  const [sqlOpen, setSqlOpen] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const fetchMessages = async (reportIdArg: number) => {
    setLoadingMessages(true);
    setLoadError(null);
    try {
      const res = await dynamicReportsAPI.messages(reportIdArg, selectedProjectId);
      setMessages(res.data.messages ?? []);
    } catch (e) {
      const anyErr = e as any;
      setLoadError(anyErr?.response?.data?.message || anyErr?.message || 'Could not load chat history.');
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    fetchMessages(reportId);
    setDraft('');
    setSendError(null);
  }, [reportId]);

  // Auto-scroll to the latest turn on each update.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, sending]);

  const send = async () => {
    const prompt = draft.trim();
    if (!prompt || sending) return;
    setSending(true);
    setSendError(null);
    setSendAlternatives([]);
    setSendFailedSql(null);
    setSendFailedSqlOpen(false);
    // Optimistic: show the user's message + a placeholder so the chat feels
    // responsive while Gemini is thinking.
    const optimisticUser: ChatMessage = {
      id: -Date.now(),
      report_id: reportId,
      role: 'user',
      content: prompt,
      created_at: new Date().toISOString(),
    };
    const optimisticAssistant: ChatMessage = {
      id: -Date.now() - 1,
      report_id: reportId,
      role: 'assistant',
      content: '__thinking__',
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimisticUser, optimisticAssistant]);
    setDraft('');

    try {
      // modify uses the report's persisted project_id server-side; no
      // need to send the active project (the user could have switched
      // projects and that's irrelevant — modify always targets the same
      // project the report was created in).
      const res = await dynamicReportsAPI.modify(reportId, prompt);
      onModified(
        res.data.report,
        res.data.preview,
        res.data.parameter_options ?? undefined,
        res.data.column_display ?? undefined
      );
      // Refetch to canonicalize message ids / ordering.
      await fetchMessages(reportId);
    } catch (e) {
      const anyErr = e as any;
      const msg = anyErr?.response?.data?.message || anyErr?.message || 'Modification failed.';
      setSendError(msg);
      setSendAlternatives(errorAlternatives(e));
      setSendFailedSql(errorGeneratedSql(e));
      // The backend persists user + assistant-error turns even on failure,
      // so a refetch picks them up. If that also fails (e.g. 403), strip
      // the optimistic placeholders and surface the inline error.
      try {
        await fetchMessages(reportId);
      } catch {
        setMessages((m) => m.filter((x) => x.id > 0));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="border-b border-gray-200 bg-white px-3 py-2 text-[11px] text-gray-500">
        <button
          onClick={() => setSqlOpen((v) => !v)}
          className="font-medium text-blue-600 hover:underline"
        >
          {sqlOpen ? 'Hide generated SQL' : 'Show generated SQL'}
        </button>
        {sqlOpen && (
          <pre className="mt-2 max-h-40 overflow-auto rounded bg-gray-900 p-2 text-[10px] leading-snug text-gray-100">
            {report.generated_sql}
          </pre>
        )}
      </div>
      <div
        ref={scrollerRef}
        className="max-h-80 min-h-[8rem] space-y-2 overflow-y-auto bg-gray-50/50 px-3 py-3"
      >
        {loadingMessages && messages.length === 0 && (
          <div className="text-center text-xs text-gray-500"><InlineLoader label="Loading conversation…" size="xs" /></div>
        )}
        {loadError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
            {loadError}
          </div>
        )}
        {!loadingMessages && messages.length === 0 && !loadError && (
          <div className="text-center text-xs text-gray-500">
            No messages yet for this report.
          </div>
        )}
        {messages.map((m) => (
          <ChatBubble key={m.id} message={m} />
        ))}
      </div>

      {canEdit ? (
        <div className="border-t border-gray-200 bg-white p-3">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Refine — e.g. "make it a pie chart"`}
              rows={2}
              disabled={sending}
              className="block flex-1 resize-none rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <button
              onClick={send}
              disabled={sending || !draft.trim()}
              className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
              style={sending || !draft.trim() ? undefined : { backgroundColor: primaryColor }}
            >
              {sending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send
            </button>
          </div>
          {sendError && (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
              <div className="flex items-start gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div>{sendError}</div>
                  {sendAlternatives.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="text-[11px] font-medium text-red-900">Try one of these instead:</div>
                      {sendAlternatives.map((alt, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            setDraft(alt);
                            setSendError(null);
                            setSendAlternatives([]);
                            setSendFailedSql(null);
                            setSendFailedSqlOpen(false);
                          }}
                          className="block w-full rounded border border-red-200 bg-white px-2 py-1 text-left text-xs text-gray-800 hover:border-blue-400 hover:bg-blue-50"
                        >
                          {alt}
                        </button>
                      ))}
                    </div>
                  )}
                  {sendFailedSql && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => setSendFailedSqlOpen((v) => !v)}
                        className="text-[11px] font-medium text-red-900 hover:underline"
                      >
                        {sendFailedSqlOpen ? 'Hide generated SQL' : 'Show generated SQL'}
                      </button>
                      {sendFailedSqlOpen && (
                        <pre className="mt-1 max-h-40 overflow-auto rounded bg-gray-900 p-2 text-[10px] leading-snug text-gray-100">
                          {sendFailedSql}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="mt-1 text-[10px] text-gray-400">⌘/Ctrl + Enter to send</div>
        </div>
      ) : (
        <div className="border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
          You don't have permission to modify this report.
        </div>
      )}
    </div>
  );
}


function ChatBubble({ message }: { message: ChatMessage }) {
  // Platform-standard primary — same as other modal surfaces.
  const primaryColor = '#1B3A54';
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[80%] rounded-2xl rounded-tr-sm px-3 py-2 text-sm text-white"
          style={{ backgroundColor: primaryColor }}
        >
          {message.content}
        </div>
      </div>
    );
  }
  // Assistant turn. Three shapes:
  //   - "__thinking__"      → animated placeholder
  //   - "ERROR: <reason>"   → red bubble with alert icon
  //   - JSON {title,sql,..} → green-tinted summary
  //   - anything else       → render as plain text
  if (message.content === '__thinking__') {
    return (
      <div className="flex justify-start">
        <div className="max-w-[80%] rounded-2xl rounded-tl-sm border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
          <InlineLoader label="Thinking…" />
        </div>
      </div>
    );
  }
  if (message.content.startsWith('ERROR:')) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[80%] rounded-2xl rounded-tl-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <div className="flex items-start gap-1.5">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span className="whitespace-pre-wrap">
              {message.content.replace(/^ERROR:\s*/, '')}
            </span>
          </div>
        </div>
      </div>
    );
  }
  // Try to parse the assistant's JSON response. Fall back to raw text if
  // it's not JSON (legacy or hand-edited rows).
  let summary: string | null = null;
  try {
    const parsed = JSON.parse(message.content) as {
      title?: string;
      chart?: { type?: string };
      charts?: Array<{ type?: string }>;
    };
    if (parsed && (parsed.title || parsed.chart || parsed.charts)) {
      // Multi-chart responses (charts:[...]) win for the summary label;
      // fall back to single chart.type otherwise.
      const chartLabel =
        Array.isArray(parsed.charts) && parsed.charts.length > 0
          ? `${parsed.charts.length} charts: ${parsed.charts
              .map((c) => c?.type ?? 'table')
              .join(', ')}`
          : parsed.chart?.type ?? 'table';
      summary = parsed.title
        ? `Updated to "${parsed.title}" (${chartLabel})`
        : `Updated chart (${chartLabel})`;
    }
  } catch {
    /* fall through */
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[80%] rounded-2xl rounded-tl-sm border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800">
        <div className="flex items-start gap-1.5">
          <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-blue-600" />
          <span className="whitespace-pre-wrap">
            {summary ?? message.content}
          </span>
        </div>
      </div>
    </div>
  );
}

